import React, { useState, useEffect } from 'react';
import {
    Search,
    Moon,
    Sun,
    Menu,
    X,
    Database,
    TrendingUp,
    Users,
    Heart,
    GraduationCap,
    Building,
    Wheat,
    Code,
    BookOpen,
    TestTube,
    LayoutDashboard,
    ArrowRight,
    Filter,
    ChevronDown,
    Play,
    Copy,
    RotateCcw,
    ExternalLink,
    Globe,
    Zap,
    Activity,
    Clock,
    Briefcase,
    BarChart3,
    MapPin,
    Upload,
    Shield,
    FileText,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    LineChart as LineChartIcon
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import './INSDAPIPortal.css';

// Main App Component
function INSDAPIPortal() {
    const [darkMode, setDarkMode] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('poverty');
    const [selectedEndpoint, setSelectedEndpoint] = useState('/api/v1/demographics/population');
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState([]);
    const [currentLang, setCurrentLang] = useState('fr');
    const [codeTab, setCodeTab] = useState('curl');
    const [selectedApi, setSelectedApi] = useState(null);
    const [showApiModal, setShowApiModal] = useState(false);

    // Toggle dark mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    // Sample data for visualizations
    const povertyData = [
        { year: '2015', rate: 40.1 },
        { year: '2017', rate: 41.4 },
        { year: '2019', rate: 42.0 },
        { year: '2021', rate: 43.2 },
        { year: '2023', rate: 44.5 },
    ];

    const cottonData = [
        { year: '2020', production: 1.85 },
        { year: '2021', production: 2.02 },
        { year: '2022', production: 1.95 },
        { year: '2023', production: 2.14 },
        { year: '2024', production: 2.25 },
    ];

    const sectorData = [
        { name: 'Agriculture', value: 35, color: '#00A86B' },
        { name: 'Services', value: 42, color: '#1890ff' },
        { name: 'Industrie', value: 23, color: '#FCD116' },
    ];

    const healthData = [
        { indicator: 'Mortalité infantile', value: 48, unit: '‰', trend: 'down' },
        { indicator: 'Espérance de vie', value: 62, unit: 'ans', trend: 'up' },
        { indicator: 'Vaccination', value: 87, unit: '%', trend: 'up' },
        { indicator: 'Accès eau potable', value: 69.9, unit: '%', trend: 'up' },
    ];

    // API endpoints with detailed information - Extended for 3 demo APIs
    const endpointsData = {
        demographics: [
            { value: '/api/v1/demographics/population', label: 'Population par région' },
            { value: '/api/v1/demographics/density', label: 'Densité démographique' },
            { value: '/api/v1/demographics/age-structure', label: 'Structure par âge' },
            { value: '/api/v1/demographics/migration', label: 'Mouvements migratoires' },
        ],
        sirene: [
            { value: '/api/v1/sirene/search', label: 'Recherche entreprise' },
            { value: '/api/v1/sirene/siren/{siren}', label: 'Détails entreprise' },
            { value: '/api/v1/sirene/sectors', label: 'Statistiques par secteur' },
            { value: '/api/v1/sirene/establishments', label: 'Établissements par région' },
        ],
        agriculture: [
            { value: '/api/v1/agriculture/cotton', label: 'Production de coton' },
            { value: '/api/v1/agriculture/cereals', label: 'Production céréalière' },
            { value: '/api/v1/agriculture/livestock', label: 'Élevage' },
            { value: '/api/v1/agriculture/prices', label: 'Prix des denrées' },
        ],
        economy: [
            { value: '/api/v1/economy/gdp', label: 'PIB et croissance' },
            { value: '/api/v1/economy/inflation', label: 'Taux d\'inflation' },
            { value: '/api/v1/economy/employment', label: 'Emploi et chômage' },
        ],
        poverty: [
            { value: '/api/v1/poverty/rates', label: 'Taux de pauvreté' },
            { value: '/api/v1/poverty/regional', label: 'Pauvreté par région' },
        ],
        bdm: [
            { value: '/api/v1/bdm/series/{code}', label: 'Série chronologique' },
            { value: '/api/v1/bdm/ihpc', label: 'Indice IHPC' },
            { value: '/api/v1/bdm/indicators', label: 'Liste des indicateurs' },
        ],
        localdata: [
            { value: '/api/v1/localdata/commune/{code}', label: 'Données communales' },
            { value: '/api/v1/localdata/regions', label: 'Données régionales' },
            { value: '/api/v1/localdata/provinces', label: 'Données provinciales' },
        ],
        health: [
            { value: '/api/v1/health/mortality', label: 'Mortalité infantile' },
            { value: '/api/v1/health/vaccination', label: 'Taux de vaccination' },
            { value: '/api/v1/health/infrastructure', label: 'Infrastructures sanitaires' },
        ]
    };

    // Enhanced Sample API responses for 3 demo APIs
    const sampleResponses = {
        '/api/v1/demographics/population': {
            status: 200,
            data: {
                indicator: 'population_totale',
                source: 'INSD - Recensement 2019',
                last_updated: '2023-12-01',
                frequency: 'annual',
                unit: 'habitants',
                values: [
                    { year: 2019, region: 'National', value: 20870060 },
                    { year: 2020, region: 'National', value: 21510181 },
                    { year: 2021, region: 'Centre', value: 3542875 },
                    { year: 2021, region: 'Nord', value: 1965642 },
                    { year: 2021, region: 'Hauts-Bassins', value: 2216842 },
                    { year: 2021, region: 'Boucle du Mouhoun', value: 1854291 },
                ],
            },
            meta: { count: 6, rate_limit: { limit: 60, remaining: 59, reset: '2025-01-01T12:00:00Z' } },
        },
        '/api/v1/demographics/density': {
            status: 200,
            data: {
                indicator: 'densite_population',
                source: 'INSD - Recensement 2019',
                last_updated: '2023-12-01',
                frequency: 'annual',
                unit: 'hab/km²',
                values: [
                    { region: 'Centre', density: 110.5, population: 3542875, superficie: 32067 },
                    { region: 'Nord', density: 45.2, population: 1965642, superficie: 43497 },
                    { region: 'Hauts-Bassins', density: 52.8, population: 2216842, superficie: 41981 },
                    { region: 'Cascades', density: 38.6, population: 812463, superficie: 21056 },
                ],
            },
            meta: { count: 4, rate_limit: { limit: 60, remaining: 58 } }
        },
        '/api/v1/sirene/search': {
            status: 200,
            data: {
                total: 1247,
                page: 1,
                limit: 10,
                results: [
                    {
                        siren: 'BF20230012345',
                        name: 'SOCIETE BURKINABE DE TEXTILE SA',
                        sector: 'Industrie textile',
                        creation_date: '2015-03-12',
                        status: 'active',
                        employees: 450,
                        region: 'Centre',
                        capital: 250000000,
                        address: 'Zone Industrielle, Ouagadougou'
                    },
                    {
                        siren: 'BF20220098765',
                        name: 'AGRO-BUSINESS BURKINA SARL',
                        sector: 'Agriculture et agroalimentaire',
                        creation_date: '2018-07-25',
                        status: 'active',
                        employees: 120,
                        region: 'Hauts-Bassins',
                        capital: 75000000,
                        address: 'Secteur 4, Bobo-Dioulasso'
                    },
                    {
                        siren: 'BF20210045678',
                        name: 'BANQUE DE DEVELOPPEMENT DU BURKINA',
                        sector: 'Finance',
                        creation_date: '2005-11-08',
                        status: 'active',
                        employees: 850,
                        region: 'Centre',
                        capital: 5000000000,
                        address: 'Avenue Kwame Nkrumah, Ouagadougou'
                    }
                ]
            },
            meta: { count: 3, total_pages: 125, rate_limit: { limit: 100, remaining: 99 } }
        },
        '/api/v1/sirene/sectors': {
            status: 200,
            data: {
                source: 'SIRENE BF - 2024',
                last_updated: '2024-12-01',
                statistics: [
                    { sector: 'Commerce', count: 45210, percentage: 36.2 },
                    { sector: 'Services', count: 31245, percentage: 25.1 },
                    { sector: 'Agriculture', count: 18965, percentage: 15.2 },
                    { sector: 'Industrie', count: 15678, percentage: 12.6 },
                    { sector: 'Construction', count: 8745, percentage: 7.0 },
                    { sector: 'Transport', count: 5234, percentage: 4.2 }
                ],
                total_enterprises: 124677,
                active_enterprises: 112209,
                inactive_enterprises: 12468
            },
            meta: { rate_limit: { limit: 100, remaining: 98 } }
        },
        '/api/v1/agriculture/cotton': {
            status: 200,
            data: {
                indicator: 'production_coton',
                source: 'INSD - Ministère de l\'Agriculture',
                last_updated: '2024-11-15',
                frequency: 'annual',
                unit: 'tonnes',
                values: [
                    { year: 2020, region: 'National', production: 780000, yield: 1.2 },
                    { year: 2021, region: 'National', production: 850000, yield: 1.3 },
                    { year: 2022, region: 'National', production: 820000, yield: 1.25 },
                    { year: 2023, region: 'National', production: 920000, yield: 1.35 },
                    { year: 2024, region: 'National', production: 950000, yield: 1.4 }
                ],
                regions_production: [
                    { region: 'Boucle du Mouhoun', production: 320000, percentage: 33.7 },
                    { region: 'Cascades', production: 280000, percentage: 29.5 },
                    { region: 'Hauts-Bassins', production: 185000, percentage: 19.5 },
                    { region: 'Sud-Ouest', production: 95000, percentage: 10.0 },
                    { region: 'Est', production: 70000, percentage: 7.3 }
                ]
            },
            meta: { count: 10, rate_limit: { limit: 60, remaining: 57 } }
        },
        '/api/v1/agriculture/cereals': {
            status: 200,
            data: {
                indicator: 'production_cereales',
                source: 'INSD - Ministère de l\'Agriculture',
                last_updated: '2024-10-01',
                frequency: 'annual',
                unit: 'tonnes',
                cereals: [
                    { type: 'Sorgho', production: 1850000, area: 1250000 },
                    { type: 'Mil', production: 1620000, area: 980000 },
                    { type: 'Maïs', production: 1250000, area: 650000 },
                    { type: 'Riz', production: 420000, area: 125000 },
                    { type: 'Fonio', production: 85000, area: 45000 }
                ],
                total_production: 5245000,
                total_area: 3120000,
                average_yield: 1.68
            },
            meta: { count: 5, rate_limit: { limit: 60, remaining: 56 } }
        },
        '/api/v1/localdata/commune/{code}': {
            status: 200,
            data: {
                commune: {
                    code: 'COM001',
                    name: 'Ouagadougou',
                    province: 'Kadiogo',
                    region: 'Centre',
                    population: 2415266,
                    superficie: 219.3,
                    maire: 'Armand Béouindé',
                    demographics: {
                        households: 402544,
                        density: 11015,
                        growth_rate: 4.2,
                        male_population: 1183480,
                        female_population: 1231786
                    },
                    infrastructure: {
                        schools: 487,
                        health_centers: 92,
                        markets: 28,
                        water_access: 89.5,
                        electricity_access: 76.3,
                        roads_paved: 65.2
                    },
                    economy: {
                        main_activities: ['Commerce', 'Services', 'Artisanat', 'Administration'],
                        unemployment_rate: 12.4,
                        formal_enterprises: 45210
                    }
                }
            },
            meta: { rate_limit: { limit: 60, remaining: 57 } }
        }
    };

    // API catalog focused on 3 main demo APIs
    const apis = [
        {
            title: 'API Démographie',
            description: 'Accédez aux données démographiques: population, densité, structure par âge, migrations.',
            category: 'Population',
            endpoints: 15,
            rateLimit: '60/min',
            status: 'active',
            icon: Users,
            gradient: 'blue-gradient',
            features: [
                'Population totale et par région',
                'Projections démographiques 2020-2050',
                'Structure par âge et sexe',
                'Mouvements migratoires'
            ],
            useCases: [
                'Planification urbaine et logement',
                'Études de marché démographiques',
                'Recherche académique en sociologie',
                'Planification des services publics'
            ],
            apiKey: 'demographics'
        },
        {
            title: 'API SIRENE BF',
            description: 'Répertoire national des entreprises et établissements. Données SIREN/SIRET.',
            category: 'Entreprises',
            endpoints: 12,
            rateLimit: '100/min',
            status: 'active',
            icon: Briefcase,
            gradient: 'purple-gradient',
            features: [
                'Recherche avancée d\'entreprises',
                'Informations détaillées (capital, effectifs)',
                'Statistiques sectorielles',
                'Historique des modifications'
            ],
            useCases: [
                'Due diligence et vérification KYC',
                'Business Intelligence et études de marché',
                'Analyse sectorielle et économique',
                'Développement d\'applications B2B'
            ],
            apiKey: 'sirene'
        },
        {
            title: 'API Agriculture',
            description: 'Données complètes sur la production agricole: coton, céréales, élevage, prix.',
            category: 'Agriculture',
            endpoints: 22,
            rateLimit: '60/min',
            status: 'active',
            icon: Wheat,
            gradient: 'green-gradient',
            features: [
                'Production de coton par région',
                'Statistiques céréalières détaillées',
                'Prix des denrées agricoles',
                'Superficies cultivées et rendements'
            ],
            useCases: [
                'Analyse de marchés agricoles',
                'Prévisions de récolte et sécurité alimentaire',
                'Politique agricole et subventions',
                'Recherche agronomique'
            ],
            apiKey: 'agriculture'
        },
        {
            title: 'API Local Data',
            description: 'Données au niveau communal pour 302 communes. Indicateurs socio-économiques locaux.',
            category: 'Données locales',
            endpoints: 18,
            rateLimit: '60/min',
            status: 'active',
            icon: MapPin,
            gradient: 'teal-gradient',
            features: [
                'Données démographiques communales',
                'Infrastructures et équipements',
                'Indicateurs économiques locaux',
                'Cartographie et géolocalisation'
            ],
            useCases: [
                'Planification décentralisée',
                'Développement communal',
                'Cartographie interactive',
                'Suivi des projets locaux'
            ],
            apiKey: 'localdata'
        },
        {
            title: 'API Économie',
            description: 'Indicateurs macroéconomiques: PIB, inflation, commerce extérieur, finances publiques.',
            category: 'Économie',
            endpoints: 28,
            rateLimit: '60/min',
            status: 'active',
            icon: TrendingUp,
            gradient: 'orange-gradient',
            features: [
                'PIB et croissance économique',
                'Taux d\'inflation et indices des prix',
                'Commerce extérieur (import/export)',
                'Finances publiques et dette'
            ],
            useCases: [
                'Analyse économique et conjoncturelle',
                'Études d\'investissement et business plans',
                'Politique monétaire et budgétaire',
                'Recherche en sciences économiques'
            ],
            apiKey: 'economy'
        },
        {
            title: 'API Santé',
            description: 'Indicateurs sanitaires: mortalité, morbidité, couverture vaccinale, infrastructures.',
            category: 'Santé',
            endpoints: 18,
            rateLimit: '60/min',
            status: 'active',
            icon: Heart,
            gradient: 'red-gradient',
            features: [
                'Mortalité infantile et maternelle',
                'Prévalence des maladies',
                'Couverture vaccinale',
                'Infrastructures sanitaires'
            ],
            useCases: [
                'Planification sanitaire nationale',
                'Recherche épidémiologique',
                'Évaluation des programmes de santé',
                'Allocation des ressources médicales'
            ],
            apiKey: 'health'
        }
    ];

    // Filter APIs based on search and filters
    const filteredApis = apis.filter(api => {
        const matchesSearch = api.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            api.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilters = filters.length === 0 || filters.includes(api.category);
        return matchesSearch && matchesFilters;
    });

    // Toggle filter
    const toggleFilter = (category) => {
        if (filters.includes(category)) {
            setFilters(filters.filter(f => f !== category));
        } else {
            setFilters([...filters, category]);
        }
    };

    // Open API details modal
    const openApiDetails = (api) => {
        setSelectedApi(api);
        setShowApiModal(true);
        setApiResponse(null);

        // Set first endpoint of this API
        const apiEndpoints = endpointsData[api.apiKey];
        if (apiEndpoints && apiEndpoints.length > 0) {
            setSelectedEndpoint(apiEndpoints[0].value);
        } else {
            setSelectedEndpoint('/api/v1/demographics/population');
        }
    };

    // Execute API request
    const executeRequest = () => {
        setLoading(true);
        setTimeout(() => {
            // Find matching response or use default
            const response = sampleResponses[selectedEndpoint] || {
                status: 200,
                data: {
                    message: 'Données de démonstration',
                    endpoint: selectedEndpoint,
                    note: 'En production, cette requête retournerait les données réelles'
                },
                meta: { rate_limit: { limit: 60, remaining: 59 } }
            };
            setApiResponse(response);
            setLoading(false);
        }, 800);
    };

    // Copy to clipboard
    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        // Optionally show feedback
    };

    // Get endpoints for current API
    const getCurrentEndpoints = () => {
        if (!selectedApi) return endpointsData.demographics;
        return endpointsData[selectedApi.apiKey] || endpointsData.demographics;
    };

    // Code examples
    const codeExamples = {
        curl: `curl -X GET "https://api.insd.bf${selectedEndpoint}" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`,
        python: `import requests

url = "https://api.insd.bf${selectedEndpoint}"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)
data = response.json()
print(data)`,
        javascript: `fetch('https://api.insd.bf${selectedEndpoint}', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`
    };

    // Calculate stats for hero section
    const totalEndpoints = apis.reduce((sum, api) => sum + api.endpoints, 0);
    const activeAPIs = apis.filter(api => api.status === 'active').length;

    return (
        <div className={`app-container ${darkMode ? 'dark' : ''}`}>
            {/* Navbar */}
            <nav className="navbar">
                <div className="nav-container">
                    <div className="nav-logo">
                        <div>
                            <div className="logo-text">INSData API</div>
                            <div className="logo-subtitle">Burkina Faso</div>
                        </div>
                    </div>

                    <div className="nav-links">
                        <button className="nav-link">
                            <Code size={18} />
                            <span>Catalogue API</span>
                        </button>
                        <Link to="/documentation" className="nav-link">
                            <BookOpen size={18} />
                            <span>Documentation</span>
                        </Link>
                        <Link to="/dashboard" className="nav-link">
                            <LayoutDashboard size={18} />
                            <span>Tableau de bord</span>
                        </Link>
                    </div>

                    <div className="nav-actions">
                        <button
                            onClick={() => setCurrentLang(currentLang === 'fr' ? 'en' : 'fr')}
                            className="nav-button"
                        >
                            <Globe size={20} />
                            <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>
                                {currentLang.toUpperCase()}
                            </span>
                        </button>
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="nav-button"
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button className="login-button">
                            Se connecter
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="mobile-menu-button"
                        >
                            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div className="mobile-menu">
                        <button className="mobile-nav-link">
                            <Code size={18} />
                            <span>Catalogue API</span>
                        </button>
                        <Link to="/documentation" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                            <BookOpen size={18} />
                            <span>Documentation</span>
                        </Link>
                        <button className="mobile-nav-link">
                            <TestTube size={18} />
                            <span>Sandbox</span>
                        </button>
                        <Link to="/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                            <LayoutDashboard size={18} />
                            <span>Tableau de bord</span>
                        </Link>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-pattern"></div>
                <div className="hero-content">
                    <h1 className="hero-title">
                        Plateforme API Statistique
                    </h1>
                    <h2 className="hero-subtitle">
                        du Burkina Faso
                    </h2>
                    <p className="hero-description">
                        Accédez aux données officielles démographiques, économiques, agricoles et d'entreprises
                        via des API modernes RESTful. Développez des applications innovantes avec les
                        statistiques de l'Institut National de la Statistique et de la Démographie.
                    </p>
                    <div className="hero-buttons">
                        <button className="primary-button">
                            Commencer maintenant
                            <ArrowRight size={20} />
                        </button>
                        <Link to="/documentation" className="secondary-button">
                            Voir la documentation
                        </Link>
                    </div>

                    <div className="stats-grid">
                        {[
                            { icon: Database, label: 'Jeux de données', value: '150+' },
                            { icon: Code, label: 'Endpoints API', value: `${totalEndpoints}+` },
                            { icon: Users, label: 'APIs actives', value: `${activeAPIs}` },
                            { icon: Zap, label: 'Requêtes/jour', value: '50k+' },
                        ].map((stat, index) => (
                            <div
                                key={index}
                                className="stat-card fade-in"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <stat.icon className="stat-icon" />
                                <div className="stat-value">{stat.value}</div>
                                <div className="stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="main-content">
                {/* Search Bar */}
                <div className="search-section">
                    <div className="search-container">
                        <div className="search-input-container">
                            <Search className="search-icon" size={20} />
                            <input
                                type="text"
                                placeholder="Rechercher des APIs, indicateurs, jeux de données..."
                                className="search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="filter-button">
                            <Filter size={18} />
                            <span>Filtres</span>
                            <ChevronDown size={18} />
                        </button>
                        <button className="search-button">
                            Rechercher
                        </button>
                    </div>

                    {filters.length > 0 && (
                        <div className="filter-tags">
                            {filters.map((filter) => (
                                <span
                                    key={filter}
                                    className="filter-tag"
                                >
                                    {filter}
                                    <button
                                        onClick={() => toggleFilter(filter)}
                                        className="remove-filter"
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* API Catalog */}
                <section className="fade-in">
                    <h2 className="section-title">Catalogue des API</h2>
                    <p className="section-description">
                        Explorez nos API pour accéder aux statistiques officielles du Burkina Faso
                    </p>
                    <div className="api-grid">
                        {filteredApis.map((api, index) => (
                            <div
                                key={index}
                                className="api-card fade-in"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className={`api-card-header ${api.gradient}`}>
                                    <api.icon className="api-icon" />
                                    <span className={`api-status ${api.status}`}>
                                        {api.status.toUpperCase()}
                                    </span>
                                    <h3 className="api-card-title">{api.title}</h3>
                                    <p className="api-card-category">{api.category}</p>
                                </div>

                                <div className="api-card-body">
                                    <p className="api-card-description">
                                        {api.description}
                                    </p>
                                    <div className="api-meta">
                                        <div className="api-meta-item">
                                            <Database size={16} />
                                            <span>{api.endpoints} endpoints</span>
                                        </div>
                                        <div className="api-meta-item">
                                            <Clock size={16} />
                                            <span>{api.rateLimit}</span>
                                        </div>
                                    </div>
                                    <div className="api-actions">
                                        <button
                                            className="api-explore-button"
                                            onClick={() => openApiDetails(api)}
                                        >
                                            Explorer l'API
                                        </button>
                                        <button className="api-external-button">
                                            <ExternalLink size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Data Visualization */}
                <section className="viz-section fade-in">
                    <h2 className="section-title">Visualisation des données en direct</h2>

                    <div className="viz-tabs">
                        {['poverty', 'agriculture', 'economy', 'health'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`viz-tab ${activeTab === tab ? 'active' : ''}`}
                            >
                                {tab === 'poverty' && 'Pauvreté'}
                                {tab === 'agriculture' && 'Agriculture'}
                                {tab === 'economy' && 'Économie'}
                                {tab === 'health' && 'Santé'}
                            </button>
                        ))}
                    </div>

                    <div className="viz-content">
                        {activeTab === 'poverty' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Évolution du taux de pauvreté (2015-2023)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={povertyData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="year" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="rate"
                                            stroke="#EF2B2D"
                                            strokeWidth={3}
                                            name="Taux de pauvreté (%)"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'agriculture' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Production de coton (millions de balles)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={cottonData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="year" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="production" fill="#00A86B" name="Production (M balles)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'economy' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Répartition sectorielle du PIB
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={sectorData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={(entry) => `${entry.name}: ${entry.value}%`}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {sectorData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'health' && (
                            <div className="fade-in">
                                <div className="health-grid">
                                    {healthData.map((item, index) => (
                                        <div
                                            key={item.indicator}
                                            className="health-card fade-in"
                                            style={{ animationDelay: `${index * 100}ms` }}
                                        >
                                            <div className="health-indicator">{item.indicator}</div>
                                            <div className="health-value">
                                                {item.value} {item.unit}
                                            </div>
                                            <div className={`health-trend ${item.trend}`}>
                                                {item.trend === 'up' ? '↑' : '↓'} Tendance {item.trend === 'up' ? 'positive' : 'négative'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Interactive Console */}
                {showApiModal && selectedApi && (
                    <section className="console-section fade-in">
                        <div className="modal-header">
                            <h2 className="section-title">
                                {selectedApi.title} - Console interactive
                            </h2>
                            <button
                                onClick={() => setShowApiModal(false)}
                                className="close-modal-button"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="api-details-grid">
                            <div className="api-detail-section">
                                <h3>Fonctionnalités</h3>
                                <ul>
                                    {selectedApi.features.map((feature, idx) => (
                                        <li key={idx}>{feature}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="api-detail-section">
                                <h3>Cas d'usage</h3>
                                <ul>
                                    {selectedApi.useCases.map((useCase, idx) => (
                                        <li key={idx}>{useCase}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="request-builder">
                            <div className="request-controls">
                                <span className="request-method">GET</span>
                                <select
                                    className="endpoint-select"
                                    value={selectedEndpoint}
                                    onChange={(e) => setSelectedEndpoint(e.target.value)}
                                >
                                    {getCurrentEndpoints().map((endpoint) => (
                                        <option key={endpoint.value} value={endpoint.value}>
                                            {endpoint.label}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={executeRequest}
                                    disabled={loading}
                                    className="execute-button"
                                >
                                    <Play size={18} />
                                    {loading ? 'Chargement...' : 'Exécuter'}
                                </button>
                                <button
                                    onClick={() => {
                                        setApiResponse(null);
                                        setLoading(false);
                                    }}
                                    className="reset-button"
                                >
                                    <RotateCcw size={18} />
                                    Réinitialiser
                                </button>
                            </div>

                            <div>
                                <div className="code-tabs">
                                    {['curl', 'python', 'javascript'].map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setCodeTab(tab)}
                                            className={`code-tab ${codeTab === tab ? 'active' : ''}`}
                                        >
                                            {tab === 'curl' && 'cURL'}
                                            {tab === 'python' && 'Python'}
                                            {tab === 'javascript' && 'JavaScript'}
                                        </button>
                                    ))}
                                </div>
                                <div className="code-container">
                                    <button
                                        onClick={() => copyCode(codeExamples[codeTab])}
                                        className="copy-button"
                                    >
                                        <Copy size={14} />
                                        Copier
                                    </button>
                                    <pre className="code-block">
                                        <code>{codeExamples[codeTab]}</code>
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {apiResponse && (
                            <div className="response-section fade-in">
                                <div className="response-header">
                                    <h3 className="section-title">Réponse de l'API</h3>
                                    <div style={{display: 'flex', gap: '0.5rem'}}>
                                        <span className="response-status">{apiResponse.status} OK</span>
                                        <span className="response-time">~800ms</span>
                                    </div>
                                </div>
                                <div className="code-container">
                                    <button
                                        onClick={() => copyCode(JSON.stringify(apiResponse, null, 2))}
                                        className="copy-button"
                                    >
                                        <Copy size={14} />
                                        Copier JSON
                                    </button>
                                    <pre className="code-block">
                                        <code>{JSON.stringify(apiResponse, null, 2)}</code>
                                    </pre>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* CTA Section */}
                <section className="cta-section fade-in">
                    <h2 className="section-title">Prêt à commencer ?</h2>
                    <p className="section-description">
                        Créez votre compte gratuit et obtenez votre clé API en moins de 2 minutes.
                        Accédez immédiatement à toutes nos données ouvertes.
                    </p>
                    <div className="cta-buttons">
                        <button className="primary-button">
                            Créer un compte gratuit
                        </button>
                        <Link to="/documentation" className="secondary-button" style={{color: '#2d3748', borderColor: '#e2e8f0'}}>
                            Consulter la documentation
                        </Link>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-column">
                        <h3>À propos</h3>
                        <p>
                            Plateforme API de l'Institut National de la Statistique et de la Démographie
                            du Burkina Faso. Accès programmatique aux données officielles.
                        </p>
                    </div>
                    <div className="footer-column">
                        <h3>Ressources</h3>
                        <ul>
                            <li><a href="#">Documentation complète</a></li>
                            <li><a href="#">Guides de démarrage</a></li>
                            <li><a href="#">Exemples de code</a></li>
                            <li><a href="#">FAQ</a></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Support</h3>
                        <ul>
                            <li><a href="#">Contact technique</a></li>
                            <li><a href="#">Forum communautaire</a></li>
                            <li><a href="#">Statut du service</a></li>
                            <li><a href="#">Signaler un problème</a></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Légal</h3>
                        <ul>
                            <li><a href="#">Conditions d'utilisation</a></li>
                            <li><a href="#">Politique de confidentialité</a></li>
                            <li><a href="#">Licence des données ouvertes</a></li>
                            <li><a href="#">Mentions légales</a></li>
                        </ul>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© 2025 INSData API By ADE- Institut National de la Statistique et de la Démographie du Burkina Faso. Tous droits réservés.</p>
                </div>
            </footer>
        </div>
    );
}

export default INSDAPIPortal;